/**
 * Event Team permission helpers (backend doc §4).
 *
 * Groups (tesseract-auth), same family pattern as the build team:
 *
 * | Action                    | argus_event_panel | argus_event_lead | argus_event_admin |
 * |---------------------------|-------------------|------------------|-------------------|
 * | event_read                | ✅                | ✅               | ✅                |
 * | event_create / event_edit | ✅                | ✅               | ✅                |
 * | event_lead                | ❌                | ✅               | ✅                |
 * | event_delete / event_admin| ❌                | ❌               | ✅                |
 * | event_points (manager)    | ❌                | ❌               | ✅                |
 *
 * `SPY_Super_admin` bypasses everything. Kanban panels additionally need the
 * caller's kanban permission for the eventteam board
 * (`argus.kanban.read|write|manage.eventteam`).
 *
 * Client-side checks are for UX only — the server validates every mutation.
 */

import type { Identity } from '../types/auth';
import {
  getLimitedTesseractTeamAccess,
  teamStaffOnlyKanbanRead,
  teamStaffOnlyKanbanWrite,
  TESSERACT_TEAM_EVENT,
} from './tesseractTeamAccess';

export interface EventTeamPerms {
  /** `event_read` — view everything. */
  canView: boolean;
  /**
   * `event_create` / `event_edit` — create/edit events, projects, comments, media,
   * web links, tasks, tags on/off, links; self-assign/unassign; watch/unwatch.
   */
  canCreate: boolean;
  /**
   * `event_lead` — assign others, workflows, scheduling, participants, retros,
   * clearance, kanban bridge links + promote, with-sync companions.
   */
  canLead: boolean;
  /** `event_delete` / `event_admin` — delete anything, tag CRUD, roster CRUD, prefix requirement overlays. */
  canAdmin: boolean;
  /** `event_points` — award/adjust/delete points; grant/revoke prefixes (the event manager). */
  canPoints: boolean;
  /** Tesseract `eventteam` staff-team-only user (no JWT groups) — read-only surface. */
  isLimitedEventTeamStaff: boolean;
}

const PANEL_GROUPS = ['argus_event_panel', 'argus_event_lead', 'argus_event_admin', 'SPY_Super_admin'];
const LEAD_GROUPS = ['argus_event_lead', 'argus_event_admin', 'SPY_Super_admin'];
const ADMIN_GROUPS = ['argus_event_admin', 'SPY_Super_admin'];

function groupsOf(identity: Identity | null): string[] {
  const groups = identity?.raw?.custom?.groups;
  return Array.isArray(groups) ? groups : [];
}

function hasAny(identity: Identity | null, target: string[]): boolean {
  const groups = groupsOf(identity);
  return target.some((g) => groups.includes(g));
}

export function getEventTeamPerms(identity: Identity | null, isDev: boolean): EventTeamPerms {
  if (isDev) {
    return {
      canView: true,
      canCreate: true,
      canLead: true,
      canAdmin: true,
      canPoints: true,
      isLimitedEventTeamStaff: false,
    };
  }
  if (!identity) {
    return {
      canView: false,
      canCreate: false,
      canLead: false,
      canAdmin: false,
      canPoints: false,
      isLimitedEventTeamStaff: false,
    };
  }

  const limited = getLimitedTesseractTeamAccess(identity, isDev);
  const isLimitedEventTeamStaff =
    limited != null && !limited.calendarViewOnly && limited.role === TESSERACT_TEAM_EVENT;

  const canViewByGroup = hasAny(identity, PANEL_GROUPS);
  const canAdmin = hasAny(identity, ADMIN_GROUPS);

  return {
    // Mirrors the build-team precedent: staff-team-only users may browse read-only.
    canView: canViewByGroup || isLimitedEventTeamStaff,
    canCreate: canViewByGroup,
    canLead: hasAny(identity, LEAD_GROUPS),
    canAdmin,
    // `event_points` is carried by the admin group (assign `argus_event_admin` to the event manager only).
    canPoints: canAdmin,
    isLimitedEventTeamStaff,
  };
}

export interface EventKanbanPerms {
  /** May read eventteam boards/cards (needed for the Workspaces panels). */
  kanbanRead: boolean;
  /** May mutate eventteam boards/cards (link/promote/adopt/sync). */
  kanbanWrite: boolean;
}

const KANBAN_TEAM = 'eventteam';

export function getEventKanbanPerms(identity: Identity | null, isDev: boolean): EventKanbanPerms {
  if (isDev) return { kanbanRead: true, kanbanWrite: true };
  if (!identity) return { kanbanRead: false, kanbanWrite: false };

  const groups = groupsOf(identity);
  if (groups.includes('SPY_Super_admin')) return { kanbanRead: true, kanbanWrite: true };

  const permissions: string[] = Array.isArray(identity.raw?.custom?.permissions)
    ? identity.raw.custom.permissions
    : [];

  const has = (action: 'read' | 'write' | 'manage') =>
    permissions.includes(`argus.kanban.${action}.${KANBAN_TEAM}`) ||
    groups.includes(`argus_kanban_${action}_${KANBAN_TEAM}`);

  const write = has('write') || has('manage') || teamStaffOnlyKanbanWrite(identity, isDev, KANBAN_TEAM);
  const read = write || has('read') || teamStaffOnlyKanbanRead(identity, isDev, KANBAN_TEAM);
  return { kanbanRead: read, kanbanWrite: write };
}
